import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Blog, BlogService } from "../../../client/api";
import { ClavaContext } from "../../../config/contexts";
import { translate } from "../../../config/translator";
import EmptyState from "../../components/EmptyState";
import NewsDetailModal from "../../components/NewsDetailModal";
import NewsItem from "../../components/NewsPanel/NewsItem";
import BackButton from "../../components/BackButton";

const NEWS_LIMIT = 10;

const AllNews: React.FC = () => {
  const { l, user } = useContext(ClavaContext);
  const areaOfInterestId = user?.areaOfInterest?.id;

  const [news, setNews] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [selectedNews, setSelectedNews] = useState<Blog | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const pageRef = useRef(1);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const fetchNews = useCallback(
    async (offset: number, limit: number): Promise<boolean> => {
      if (!areaOfInterestId) {
        return false;
      }

      if (offset === 0) {
        setLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      setError(null);

      try {
        const result = await BlogService.getBlogsBlogGet(limit, offset, areaOfInterestId);
        const newItems = result || [];
        const hasMoreItems = newItems.length === limit;

        if (offset === 0) {
          setNews(newItems);
          setHasMore(hasMoreItems);
        } else {
          setNews((prev) => {
            const existingIds = new Set(prev.map((item) => item.id));
            const filteredItems = newItems.filter((item) => !existingIds.has(item.id));
            return [...prev, ...filteredItems];
          });
          setHasMore(hasMoreItems);
        }

        return hasMoreItems;
      } catch (err) {
        setError(err instanceof Error ? err : new Error("Failed to fetch news"));
        if (offset === 0) {
          setNews([]);
        }
        setHasMore(false);
        return false;
      } finally {
        setLoading(false);
        setIsLoadingMore(false);
      }
    },
    [areaOfInterestId]
  );

  useEffect(() => {
    if (news.length === 0 && areaOfInterestId) {
      fetchNews(0, NEWS_LIMIT).catch(() => undefined);
      pageRef.current = 1;
      setHasMore(true);
    }
  }, [areaOfInterestId, fetchNews, news.length]);

  useEffect(() => {
    const currentRef = loadMoreRef.current;
    if (!currentRef || !hasMore) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !isLoadingMore && !loading) {
          const loadMore = async () => {
            const offset = pageRef.current * NEWS_LIMIT;
            const hasMoreItems = await fetchNews(offset, NEWS_LIMIT);
            if (hasMoreItems) {
              pageRef.current += 1;
            }
          };
          loadMore().catch(() => undefined);
        }
      },
      {
        root: null,
        rootMargin: "200px",
        threshold: 0.1,
      }
    );

    observer.observe(currentRef);

    return () => {
      observer.unobserve(currentRef);
    };
  }, [fetchNews, hasMore, isLoadingMore, loading]);

  const handleSelectNews = useCallback((item: Blog) => {
    setSelectedNews(item);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedNews(null);
  }, []);

  if (!areaOfInterestId) {
    return null;
  }

  if (loading && news.length === 0) {
    return (
      <div className="w-full px-6 xl:px-8 mb-8">
        <div className="max-w-6xl xl:max-w-7xl mx-auto">
          <div className="relative mb-6">
            <BackButton absolute={false} />
            <h1 className="text-2xl font-semibold text-font dark:text-font-dark">
              {translate("news", l)}
            </h1>
          </div>
          <div className="grid grid-cols-3 xl:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="w-full animate-pulse">
                <div className="w-full bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark rounded-xl overflow-hidden">
                  <div className="w-full aspect-video bg-bg-100 dark:bg-bg-100-dark" />
                  <div className="p-4">
                    <div className="h-4 bg-bg-100 dark:bg-bg-100-dark rounded w-3/4 mb-2" />
                    <div className="h-4 bg-bg-100 dark:bg-bg-100-dark rounded w-1/2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error && news.length === 0) {
    return (
      <div className="w-full px-6 xl:px-8 mb-8">
        <div className="max-w-6xl xl:max-w-7xl mx-auto">
          <EmptyState
            message={error.message || "Failed to load news"}
            className="w-full"
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="w-full px-6 xl:px-8 mb-8">
        <div className="max-w-6xl xl:max-w-7xl mx-auto">
          <div className="relative mb-6">
            <BackButton absolute={false} />
            <h1 className="text-2xl font-semibold text-font dark:text-font-dark">
              {translate("news", l)}
            </h1>
          </div>

          <div className="grid grid-cols-3 xl:grid-cols-4 gap-4">
            {news.map((item) => (
              <NewsItem
                key={item.id}
                news={item}
                onClick={() => handleSelectNews(item)}
                variant="grid"
              />
            ))}

            {news.length === 0 && !loading && (
              <div className="col-span-full">
                <EmptyState messageKey="noNews" className="w-full" />
              </div>
            )}
          </div>

          {isLoadingMore && (
            <div className="w-full flex justify-center py-8">
              <div className="animate-pulse text-font-600 dark:text-font-600-dark text-sm">
                {translate("loading", l)}...
              </div>
            </div>
          )}

          {hasMore && <div ref={loadMoreRef} className="h-4" />}
        </div>
      </div>

      {selectedNews && (
        <NewsDetailModal news={selectedNews} onClose={handleCloseModal} />
      )}
    </>
  );
};

export default AllNews;

